import { useRef, useState } from "react";
import { UploadCloud, FileText, Loader2 } from "lucide-react";

export default function FileUpload({ onFile, loading }) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [selected, setSelected] = useState(null);

  const handleFiles = (files) => {
    if (!files?.length) return;
    const f = files[0];
    const okTypes = [
      "image/jpeg", "image/png", "image/webp", "application/pdf",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    const nameOk = /\.(jpe?g|png|webp|pdf|xlsx|xls)$/i.test(f.name.trim());
    if (!okTypes.includes(f.type) && !nameOk) {
      alert("Please upload a JPG, PNG, WEBP, PDF, or Excel (.xls / .xlsx) file.");
      return;
    }
    setSelected(f);
    onFile(f);
  };

  return (
    <div
      data-testid="file-upload-dropzone"
      onDragEnter={(e) => {
        e.preventDefault();
        setDragActive(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={() => setDragActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragActive(false);
        handleFiles(e.dataTransfer.files);
      }}
      onClick={() => inputRef.current?.click()}
      className={`relative cursor-pointer border-2 border-dashed transition-colors duration-200 p-10 md:p-14 min-h-[280px] flex flex-col items-center justify-center text-center bg-slate-50 rounded-xl ${
        dragActive
          ? "dropzone-active"
          : "border-slate-300 hover:border-[#2563EB] hover:bg-blue-50/50"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        data-testid="file-upload-input"
        accept="image/jpeg,image/png,image/webp,application/pdf,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {renderDropzoneContent({ loading, selected })}
    </div>
  );
}

function renderDropzoneContent({ loading, selected }) {
  if (loading) {
    return (
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="w-8 h-8 text-[#2563EB] animate-spin" />
        <div className="heading text-lg font-medium">Analyzing packing list with AI…</div>
        <div className="mono text-xs text-[#64748B]">Gemini 3 Flash is reading your file. This can take 20–60 seconds.</div>
      </div>
    );
  }
  if (selected) {
    return (
      <div className="flex flex-col items-center gap-2">
        <FileText className="w-10 h-10 text-[#2563EB]" />
        <div className="mono text-sm">{selected.name}</div>
        <div className="overline">Click or drop another file to replace</div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-3">
      <UploadCloud className="w-12 h-12 text-[#2563EB]" strokeWidth={1.5} />
      <div className="heading text-xl md:text-2xl font-semibold">
        Drop your packing list here
      </div>
      <div className="text-sm text-[#64748B] max-w-sm">
        or <span className="text-[#2563EB] underline underline-offset-4">click to browse</span>. Supports JPG, PNG, WEBP, PDF, and Excel (.xls / .xlsx).
      </div>
      <div className="mono text-[10px] tracking-widest text-[#64748B] uppercase mt-2">
        Max 20MB · Data processed once, never stored raw
      </div>
    </div>
  );
}
